import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "searchInput", "frame", "filterBtn", "viewBtn",
    "regionCheckbox", "regionToggleAll", "regionBadge",
    "dateFrom", "dateTo", "datePresets",
    "filterBadge", "exportButton",
    "statusReportModal", "statusReportCheckbox", "statusReportExportBtn"
  ]
  static values = {
    viewMode: { type: String, default: "thin" },
    savedFilters: { type: Array, default: [] },
    savedSort: { type: String, default: "bid_deadline" },
    savedRegions: { type: Array, default: [] },
    savedDateFrom: { type: String, default: "" },
    savedDateTo: { type: String, default: "" },
    savedDatePreset: { type: String, default: "" }
  }

  connect() {
    this._debounceTimer = null
    this._currentFilters = this.savedFiltersValue.length ? [...this.savedFiltersValue] : []
    this._currentSort = this.savedSortValue || "bid_deadline"
    this._currentDatePreset = this.savedDatePresetValue || ""

    // Restore saved date range
    if (this._currentDatePreset) {
      this._applyPresetDates(this._currentDatePreset)
    } else if (this.savedDateFromValue || this.savedDateToValue) {
      if (this.savedDateFromValue && this.hasDateFromTarget) this.dateFromTarget.value = this.savedDateFromValue
      if (this.savedDateToValue && this.hasDateToTarget) this.dateToTarget.value = this.savedDateToValue
    }

    this._updateFilterBadges()
    this._updateRegionBadge()
    this._updateDatePresetChips()
    this._updateFilterBadgeCount()
  }

  // Debounced search
  search() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this._reload(), 300)
  }

  // Status filter from badge clicks (multi-select toggle)
  filter(event) {
    const value = event.currentTarget.dataset.filter
    const idx = this._currentFilters.indexOf(value)
    if (idx >= 0) {
      this._currentFilters.splice(idx, 1)
    } else {
      this._currentFilters.push(value)
    }

    this._updateFilterBadges()
    this._reload()
  }

  _updateFilterBadges() {
    const badgeClasses = ["badge-status-bid_open", "badge-status-bid_closed"]

    this.filterBtnTargets.forEach((b) => {
      const ownClass = b.dataset.badgeClass
      badgeClasses.forEach((c) => b.classList.remove(c))

      const inactiveClasses = ["bg-white", "text-text-muted", "border-gray-300",
        "hover:text-gray-700", "hover:border-gray-400"]

      if (this._currentFilters.includes(b.dataset.filter)) {
        b.classList.add(ownClass, "border-transparent")
        b.classList.remove(...inactiveClasses)
      } else {
        b.classList.add(...inactiveClasses)
        b.classList.remove("border-transparent")
      }
    })
  }

  // Sort from dropdown
  sort(event) {
    this._currentSort = event.currentTarget.value
    this._reload()
  }

  // View mode toggle (card / thin)
  toggleView(event) {
    const mode = event.currentTarget.dataset.viewMode
    this.viewModeValue = mode

    this.viewBtnTargets.forEach((b) => {
      if (b.dataset.viewMode === mode) {
        b.classList.add("text-brand", "font-bold")
        b.classList.remove("text-text-muted", "font-medium", "hover:text-gray-700")
      } else {
        b.classList.remove("text-brand", "font-bold")
        b.classList.add("text-text-muted", "font-medium", "hover:text-gray-700")
      }
    })

    // Save preference
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(`/bidding/save_bidding_view_mode?mode=${mode}`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken }
    })

    this._reload()
  }

  // Region filter: individual checkbox change
  filterRegions() {
    const checkboxes = this.regionCheckboxTargets
    const allChecked = checkboxes.every((cb) => cb.checked)
    this.regionToggleAllTargets.forEach((toggle) => {
      toggle.checked = allChecked
      toggle.indeterminate = !allChecked && checkboxes.some((cb) => cb.checked)
    })

    this._reload()
  }

  // Region filter: toggle all checkboxes
  toggleAllRegions(event) {
    const checked = event.currentTarget.checked
    this.regionCheckboxTargets.forEach((cb) => { cb.checked = checked })
    this.regionToggleAllTargets.forEach((toggle) => {
      toggle.checked = checked
      toggle.indeterminate = false
    })
    this._reload()
  }

  // Date preset selection
  selectDatePreset(event) {
    const preset = event.currentTarget.dataset.preset
    this._currentDatePreset = preset

    this._applyPresetDates(preset)
    this._updateDatePresetChips()
    this._reload()
  }

  // Custom date range filter
  filterDates() {
    this._currentDatePreset = ""
    this._updateDatePresetChips()
    this._reload()
  }

  _applyPresetDates(preset) {
    const today = new Date()
    let from = ""
    let to = ""

    if (preset === "30" || preset === "90" || preset === "365") {
      const days = parseInt(preset)
      const fromDate = new Date(today)
      fromDate.setDate(fromDate.getDate() - days)
      from = this._formatDate(fromDate)
      to = this._formatDate(today)
    } else if (preset === "ytd") {
      from = `${today.getFullYear()}-01-01`
      to = this._formatDate(today)
    }

    if (this.hasDateFromTarget) this.dateFromTarget.value = from
    if (this.hasDateToTarget) this.dateToTarget.value = to
  }

  _updateDatePresetChips() {
    if (!this.hasDatePresetsTarget) return
    const buttons = this.datePresetsTarget.querySelectorAll("button")
    const activeClasses = ["bg-brand/10", "text-brand", "font-medium", "border-brand/30"]
    const inactiveClasses = ["text-gray-500", "hover:bg-gray-50", "border-gray-200"]

    buttons.forEach((btn) => {
      const isActive = btn.dataset.preset === (this._currentDatePreset || "")
      btn.classList.remove(...activeClasses, ...inactiveClasses)
      if (isActive) {
        btn.classList.add(...activeClasses)
      } else {
        btn.classList.add(...inactiveClasses)
      }
    })
  }

  // Clear all filters
  clearFilters() {
    // Reset status filters to all
    this._currentFilters = []
    this._updateFilterBadges()

    // Reset regions to all
    if (this.hasRegionCheckboxTarget) {
      this.regionCheckboxTargets.forEach((cb) => { cb.checked = true })
    }
    if (this.hasRegionToggleAllTarget) {
      this.regionToggleAllTargets.forEach((t) => { t.checked = true; t.indeterminate = false })
    }

    // Reset date to all
    this._currentDatePreset = ""
    if (this.hasDateFromTarget) this.dateFromTarget.value = ""
    if (this.hasDateToTarget) this.dateToTarget.value = ""
    this._updateDatePresetChips()

    // Reset sort to default
    this._currentSort = "name"
    const sortSelect = this.element.querySelector(".sort-select")
    if (sortSelect) sortSelect.value = "name"

    this._reload()
  }

  _formatDate(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  // PDF Status Report — open modal
  exportStatusReport() {
    const dropdown = this.element.querySelector('[data-controller="dropdown"] [data-dropdown-target="menu"]')
    if (dropdown) dropdown.classList.add("hidden")

    if (this.hasStatusReportModalTarget) {
      this.statusReportModalTarget.classList.remove("hidden")
    }
  }

  // Close status report modal
  closeStatusReportModal() {
    if (this.hasStatusReportModalTarget) {
      this.statusReportModalTarget.classList.add("hidden")
    }
  }

  // Toggle all status report checkboxes + textareas
  toggleAllStatusReport(event) {
    const checked = event.currentTarget.checked
    this.statusReportCheckboxTargets.forEach((cb) => {
      cb.checked = checked
      const textarea = cb.closest("div.mb-2")?.querySelector("textarea")
      if (textarea) {
        textarea.classList.toggle("hidden", !checked)
      }
    })
  }

  // Show/hide textarea when individual checkbox toggled
  toggleStatusTextarea(event) {
    const cb = event.currentTarget
    const textarea = cb.closest("div.mb-2")?.querySelector("textarea")
    if (textarea) {
      textarea.classList.toggle("hidden", !cb.checked)
    }
  }

  // Confirm status report export — save data then open PDF
  confirmStatusReportExport() {
    const items = this.statusReportCheckboxTargets
      .filter((cb) => cb.checked)
      .map((cb) => {
        const textarea = cb.closest("div.mb-2")?.querySelector("textarea")
        return {
          project_id: parseInt(cb.value),
          text: textarea ? textarea.value : ""
        }
      })

    if (items.length === 0) return

    this._showStatusReportSpinner()

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch("/bids/set_data_for_status_report", {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ data: JSON.stringify(items) })
    })
      .then((response) => {
        if (response.ok) {
          window.open("/bids/bids_status_report_export.pdf", "_blank")
          this._showToast("Status report exported successfully")
        } else {
          throw new Error("Export failed")
        }
      })
      .catch(() => this._showToast("Failed to export. Please try again.", "error"))
      .finally(() => {
        this._hideStatusReportSpinner()
        this.closeStatusReportModal()
      })
  }

  _showStatusReportSpinner() {
    if (!this.hasStatusReportExportBtnTarget) return
    const btn = this.statusReportExportBtnTarget
    btn._originalHTML = btn.innerHTML
    btn.style.width = `${btn.offsetWidth}px`
    btn.classList.add("justify-center")
    btn.disabled = true
    btn.innerHTML = `<svg class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
  }

  _hideStatusReportSpinner() {
    if (!this.hasStatusReportExportBtnTarget) return
    const btn = this.statusReportExportBtnTarget
    if (btn._originalHTML) {
      btn.innerHTML = btn._originalHTML
      delete btn._originalHTML
    }
    btn.style.width = ""
    btn.classList.remove("justify-center")
    btn.disabled = false
  }

  _showExportSpinner() {
    if (!this.hasExportButtonTarget) return
    const btn = this.exportButtonTarget
    btn._originalHTML = btn.innerHTML
    btn.style.width = `${btn.offsetWidth}px`
    btn.classList.add("justify-center")
    btn.disabled = true
    btn.innerHTML = `<svg class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
  }

  _hideExportSpinner() {
    if (!this.hasExportButtonTarget) return
    const btn = this.exportButtonTarget
    if (btn._originalHTML) {
      btn.innerHTML = btn._originalHTML
      delete btn._originalHTML
    }
    btn.style.width = ""
    btn.classList.remove("justify-center")
    btn.disabled = false
  }

  _reload() {
    this._updateFilterBadges()
    this._updateRegionBadge()
    this._updateFilterBadgeCount()
    const frame = this.frameTarget
    frame.src = this._buildUrl()
  }

  _buildUrl() {
    const endpoint = `/bidding/list_projects`

    const url = new URL(endpoint, window.location.origin)
    url.searchParams.set("page", "1")
    url.searchParams.set("format", "html")

    const q = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""
    if (q) url.searchParams.set("q", q)

    if (this._currentFilters.length > 0) {
      this._currentFilters.forEach((f) => url.searchParams.append("filter[]", f))
    }

    url.searchParams.set("sort", this._currentSort || "bid_deadline")
    url.searchParams.set("view_mode", this.viewModeValue)

    // Region filter params
    if (this.hasRegionCheckboxTarget) {
      const selected = this.regionCheckboxTargets
        .filter((cb) => cb.checked)
        .map((cb) => cb.value)
      const total = this.regionCheckboxTargets.length

      if (selected.length < total) {
        selected.forEach((r) => url.searchParams.append("regions[]", r))
      }
    }

    // Date filter params
    if (this.hasDateFromTarget && this.dateFromTarget.value) {
      url.searchParams.set("date_from", this.dateFromTarget.value)
    }
    if (this.hasDateToTarget && this.dateToTarget.value) {
      url.searchParams.set("date_to", this.dateToTarget.value)
    }
    if (this._currentDatePreset) {
      url.searchParams.set("date_preset", this._currentDatePreset)
    }

    return url.toString()
  }

  _updateRegionBadge() {
    if (this.hasRegionBadgeTarget && this.hasRegionCheckboxTarget) {
      const selected = this.regionCheckboxTargets.filter((cb) => cb.checked).length
      const total = this.regionCheckboxTargets.length
      if (selected < total) {
        this.regionBadgeTarget.textContent = `${selected}/${total}`
        this.regionBadgeTarget.classList.remove("hidden")
      } else {
        this.regionBadgeTarget.classList.add("hidden")
      }
    }
  }

  _updateFilterBadgeCount() {
    if (!this.hasFilterBadgeTarget) return
    let count = 0

    // Status: count if not all-or-none selected
    if (this._currentFilters.length > 0) count++

    // Region: count if not all selected
    if (this.hasRegionCheckboxTarget) {
      const selected = this.regionCheckboxTargets.filter((cb) => cb.checked).length
      const total = this.regionCheckboxTargets.length
      if (selected < total) count++
    }

    // Date: count if a date range is set
    const hasFrom = this.hasDateFromTarget && this.dateFromTarget.value
    const hasTo = this.hasDateToTarget && this.dateToTarget.value
    if (hasFrom || hasTo) count++

    if (count > 0) {
      this.filterBadgeTarget.textContent = count
      this.filterBadgeTarget.classList.remove("hidden")
      this.filterBadgeTarget.classList.add("inline-flex")
    } else {
      this.filterBadgeTarget.classList.add("hidden")
      this.filterBadgeTarget.classList.remove("inline-flex")
    }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
